// Real-Time Validation System for Survey Forms
// ============================================



/**
 * Enhanced real-time validation hook for survey components
 * @param {Object} formData - Current form data
 * @param {number} step - Current step
 * @param {string} study - Study type
 * @param {Function} setFieldErrors - Function to set field-level errors
 * @param {Function} setTouchedFields - Function to track touched fields
 * @returns {Object} Validation utilities
 */
export const useRealTimeValidation = (formData, step, study, setFieldErrors, setTouchedFields) => {

  /**
   * Validates a single field and updates its error state with immediate feedback
   * @param {string} fieldPath - The path to the field (e.g., "vasScore", "ases.asesQuestion1")
   * @param {any} value - The field value
   */
  const validateField = (fieldPath, value) => {
    // Perform field validation based on field type and rules
    let isValid = true;
    let errorMessage = '';

    // Extract field type and name from path
    const pathParts = fieldPath.split('.');
    const fieldType = pathParts.length > 1 ? pathParts[0] : 'root';
    const fieldName = pathParts.length > 1 ? pathParts[1] : pathParts[0];

    try {
      switch (fieldType) {
        case 'root':
          if (fieldName === 'vasScore') {
            // Immediate validation for VAS score
            if (!value && value !== 0) {
              isValid = false;
              errorMessage = 'Gelieve een pijnwaarde in te vullen';
            } else {
              const numValue = parseFloat(value);
              if (isNaN(numValue)) {
                isValid = false;
                errorMessage = 'Gelieve een geldig getal in te vullen';
              } else if (numValue < 0) {
                isValid = false;
                errorMessage = 'Pijnwaarde moet minimaal 0 zijn';
              } else if (numValue > 10) {
                isValid = false;
                errorMessage = 'Pijnwaarde mag maximaal 10 zijn';
              }
            }
          } else if (fieldName === 'saneScore') {
            // Immediate validation for SANE score
            if (!value && value !== 0) {
              isValid = false;
              errorMessage = 'Gelieve een score in te vullen';
            } else {
              const numValue = parseInt(value);
              if (isNaN(numValue)) {
                isValid = false;
                errorMessage = 'Gelieve een geldig getal in te vullen';
              } else if (numValue < 0) {
                isValid = false;
                errorMessage = 'Score moet minimaal 0 zijn';
              } else if (numValue > 100) {
                isValid = false;
                errorMessage = 'Score mag maximaal 100 zijn';
              }
            }
          }
          break;

        case 'ases':
          const questionNum = parseInt(fieldName.replace('asesQuestion', ''));
          if (!value || value === '') {
            isValid = false;
            errorMessage = `ASES vraag ${questionNum} is verplicht`;
          } else {
            // Additional validation for pain scale questions
            if (study === "elbow" && [2, 3, 4, 5, 6].includes(questionNum)) {
              const numValue = parseInt(value);
              if (isNaN(numValue) || numValue < 0 || numValue > 10) {
                isValid = false;
                errorMessage = 'Waarde moet tussen 0 en 10 zijn';
              }
            } else if (study !== "elbow" && questionNum === 5) {
              const numValue = parseInt(value);
              if (isNaN(numValue) || numValue < 0 || numValue > 20) {
                isValid = false;
                errorMessage = 'Waarde moet tussen 0 en 20 zijn';
              }
            } else if (study !== "elbow" && questionNum === 7) {
              const numValue = parseFloat(value);
              if (isNaN(numValue) || numValue < 0 || numValue > 10) {
                isValid = false;
                errorMessage = 'Waarde moet tussen 0 en 10 zijn';
              }
            }
          }
          break;

        case 'vr12':
          const vr12QuestionNum = parseInt(fieldName.replace('vr12Question', ''));
          if (!value || value === '') {
            isValid = false;
            errorMessage = `VR-12 vraag ${vr12QuestionNum} is verplicht`;
          }
          break;

        case 'spreop':
          const spreopQuestionNum = parseInt(fieldName.replace('spreopQuestion', ''));
          if (!value || value === '') {
            isValid = false;
            errorMessage = `Pre-operatieve vraag ${spreopQuestionNum} is verplicht`;
          }
          break;

        case 'sepostop':
          const sepostopQuestionNum = parseInt(fieldName.replace('sepostopQuestion', ''));
          if (!value || value === '') {
            isValid = false;
            errorMessage = `Post-operatieve vraag ${sepostopQuestionNum} is verplicht`;
          }
          break;

        case 'slpostop':
          const slpostopQuestionNum = parseInt(fieldName.replace('slpostopQuestion', ''));
          if (!value || value === '') {
            isValid = false;
            errorMessage = `Late post-operatieve vraag ${slpostopQuestionNum} is verplicht`;
          }
          break;

        default:
          // Default validation for unknown field types
          if (!value || value === '') {
            isValid = false;
            errorMessage = 'Dit veld is verplicht';
          }
          break;
      }

      // Update field error state immediately
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        if (isValid) {
          // Clear error immediately when field becomes valid
          newErrors[fieldPath] = null;
        } else {
          // Set error immediately when field becomes invalid
          newErrors[fieldPath] = errorMessage;
        }
        return newErrors;
      });

      return { isValid, errorMessage };

    } catch (error) {
      console.warn(`Validation error for field ${fieldPath}:`, error);
      return { isValid: true, errorMessage: '' };
    }
  };

  /**
   * Enhanced real-time validation for immediate feedback
   * @param {string} fieldPath - The path to the field
   * @param {any} value - The field value
   */
  const validateFieldRealTime = (fieldPath, value) => {
    // Mark field as touched immediately for instant feedback
    setTouchedFields(prev => ({ ...prev, [fieldPath]: true }));

    // Perform validation and return result
    return validateField(fieldPath, value);
  };

  /**
   * Validates multiple fields at once
   * @param {Object} fieldsToValidate - Object with field paths as keys and values as values
   */
  const validateMultipleFields = (fieldsToValidate) => {
    const results = {};
    Object.entries(fieldsToValidate).forEach(([fieldPath, value]) => {
      results[fieldPath] = validateField(fieldPath, value);
    });
    return results;
  };

  /**
   * Clears validation for a specific field
   * @param {string} fieldPath - The field path to clear
   */
  const clearFieldValidation = (fieldPath) => {
    setFieldErrors(prev => ({
      ...prev,
      [fieldPath]: null
    }));
  };

  /**
   * Clears all field validations
   */
  const clearAllFieldValidations = () => {
    setFieldErrors({});
    setTouchedFields({});
  };

  /**
   * Gets the current validation state for a field
   * @param {string} fieldPath - The field path
   * @param {Object} currentFieldErrors - Current field errors state
   * @param {Object} currentTouchedFields - Current touched fields state
   * @returns {Object} Validation state
   */
  const getFieldValidationState = (fieldPath, currentFieldErrors = {}, currentTouchedFields = {}) => {
    const hasError = currentFieldErrors[fieldPath] !== null && currentFieldErrors[fieldPath] !== undefined;
    const isTouched = currentTouchedFields[fieldPath] || false;

    return {
      isValid: !hasError,
      hasError: hasError && isTouched,
      errorMessage: hasError ? currentFieldErrors[fieldPath] : '',
      isTouched,
      shouldShowError: hasError && isTouched
    };
  };

  return {
    validateField,
    validateFieldRealTime,
    validateMultipleFields,
    clearFieldValidation,
    clearAllFieldValidations,
    getFieldValidationState
  };
};

/**
 * Enhanced form data handler with real-time validation
 * @param {Function} originalHandler - Original handleInputData function
 * @param {Function} validateField - Field validation function
 * @returns {Function} Enhanced handler
 */
export const createEnhancedInputHandler = (originalHandler, validateField) => {
  return (type, input, value) => {
    // Call original handler to update form data
    originalHandler(type, input, value);

    // Determine field path for validation
    const fieldPath = type === 'vas' || type === 'sane' ? input : `${type}.${input}`;

    // Perform real-time validation
    setTimeout(() => {
      validateField(fieldPath, value);
    }, 0);
  };
};

/**
 * Utility to extract field value from form data using path
 * @param {Object} formData - Form data object
 * @param {string} fieldPath - Field path (e.g., "vasScore", "ases.asesQuestion1")
 * @returns {any} Field value
 */
export const getFieldValue = (formData, fieldPath) => {
  const pathParts = fieldPath.split('.');
  if (pathParts.length === 1) {
    return formData[pathParts[0]];
  } else {
    return formData[pathParts[0]]?.[pathParts[1]];
  }
};

/**
 * Utility to check if any fields in current step have validation errors
 * @param {Object} fieldErrors - Current field errors
 * @param {Object} touchedFields - Current touched fields
 * @param {number} step - Current step
 * @param {string} study - Study type
 * @returns {boolean} True if step has errors
 */
export const stepHasValidationErrors = (fieldErrors, touchedFields, step, study) => {
  // Define which fields belong to each step
  const stepFieldMappings = {
    1: ['vasScore'],
    2: Array.from({ length: study === 'elbow' ? 18 : 17 }, (_, i) => `ases.asesQuestion${i + 1}`),
    3: ['saneScore'],
    4: ['spreop.spreopQuestion1', 'spreop.spreopQuestion2', 'spreop.spreopQuestion3', 'spreop.spreopQuestion4'],
    5: Array.from({ length: 14 }, (_, i) => `vr12.vr12Question${i + 1}`)
  };

  const stepFields = stepFieldMappings[step] || [];

  return stepFields.some(fieldPath => {
    const hasError = fieldErrors[fieldPath] && touchedFields[fieldPath];
    return hasError;
  });
};

/**
 * Utility to apply CSS classes based on validation state
 * @param {Object} validationState - Field validation state
 * @param {string} baseClassName - Base CSS class
 * @returns {string} Combined CSS classes
 */
export const getValidationClasses = (validationState, baseClassName = '') => {
  const classes = [baseClassName];

  if (validationState.shouldShowError) {
    classes.push('invalid');
  } else if (validationState.isTouched && validationState.isValid) {
    classes.push('valid');
  }

  return classes.filter(Boolean).join(' ');
};

/**
 * Get instant validation state for a field
 * @param {string} fieldPath - The field path
 * @param {Object} fieldErrors - Current field errors
 * @param {Object} touchedFields - Current touched fields
 * @param {any} fieldValue - Current field value
 * @returns {Object} Validation state with visual feedback flags
 */
export const getInstantValidationState = (fieldPath, fieldErrors, touchedFields, fieldValue) => {
  const hasError = fieldErrors[fieldPath] && touchedFields[fieldPath];
  const isValid = touchedFields[fieldPath] && !fieldErrors[fieldPath] && fieldValue !== '' && fieldValue !== null && fieldValue !== undefined;

  return {
    hasError,
    isValid,
    isTouched: touchedFields[fieldPath] || false,
    errorMessage: hasError ? fieldErrors[fieldPath] : null,
    formGroupClasses: `modern-form-group ${hasError ? 'has-error' : ''}`,
    inputClasses: (baseClass = '') => `${baseClass} ${hasError ? 'invalid' : ''}`,
    ariaProps: {
      'aria-invalid': hasError,
      'aria-describedby': hasError ? `${fieldPath.replace('.', '-')}-error` : undefined
    },
    errorId: `${fieldPath.replace('.', '-')}-error`
  };
};